// src/features/feeds/components/FeedSourceDialog.tsx

import { useMemo, useEffect } from 'react';
import { Modal, Stack, TextInput, Select, Button, Group } from '@mantine/core';
import { useForm, useController } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { MediaType } from '@lib2/model/media/MediaType';
import { usePlaylists } from '@/features/playlists/usePlaylists';
import { feedSourceFormSchema, type FeedSourceFormSchemaData } from '../schemas/FeedSourceForm.schema';
import { FEED_CONSTRAINTS } from '@/lib/constants/feed';
import type { FeedSourceData } from '@/lib/types/Feed';

interface FeedSourceDialogProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (data: FeedSourceFormSchemaData) => void;
  editSource?: FeedSourceData | null;
}

export function FeedSourceDialog({ opened, onClose, onSubmit, editSource }: FeedSourceDialogProps) {
  const { data: playlistData } = usePlaylists();

  // Filter playlists to video and video_stream types only
  const playlistOptions = useMemo(() => {
    if (!playlistData) return [];
    const allLists = [...(playlistData.customLists ?? []), ...(playlistData.tagLists ?? [])];
    return allLists
      .filter((p) => p.mediaType === MediaType.Video || p.mediaType === MediaType.VideoStream)
      .sort((a, b) => a.title.localeCompare(b.title))
      .map((p) => {
        const count = 'mediaPositions' in p ? p.mediaPositions.length : null;
        const countLabel = count !== null ? ` (${count} items)` : '';
        return {
          value: String(p.listId),
          label: `${p.title}${countLabel}`,
        };
      });
  }, [playlistData]);

  const { control, handleSubmit, reset } = useForm<FeedSourceFormSchemaData>({
    resolver: zodResolver(feedSourceFormSchema),
    defaultValues: editSource
      ? { title: editSource.title, listId: editSource.listId }
      : { title: '', listId: 0 },
  });

  // Reset form values when the dialog opens (or editSource changes)
  // useForm defaultValues are only read on mount — this ensures correct values each time
  useEffect(() => {
    if (opened) {
      reset(editSource
        ? { title: editSource.title, listId: editSource.listId }
        : { title: '', listId: 0 });
    }
  }, [opened, editSource, reset]);

  const { field: titleField, fieldState: titleState } = useController({ control, name: 'title' });
  const { field: listIdField, fieldState: listIdState } = useController({ control, name: 'listId' });

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFormSubmit = handleSubmit((data) => {
    onSubmit(data);
    handleClose();
  });

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={editSource ? 'Edit Source' : 'Add Source'}
      size="md"
    >
      <form onSubmit={handleFormSubmit}>
        <Stack gap="md">
          <TextInput
            label="Source Title"
            value={titleField.value}
            onChange={titleField.onChange}
            onBlur={titleField.onBlur}
            error={titleState.error?.message}
            description={`${titleField.value.length}/${FEED_CONSTRAINTS.SOURCE_TITLE_MAX}`}
            maxLength={FEED_CONSTRAINTS.SOURCE_TITLE_MAX}
            required
            data-autofocus
          />

          <Select
            label="Playlist"
            placeholder="Select a playlist"
            data={playlistOptions}
            value={listIdField.value > 0 ? String(listIdField.value) : null}
            onChange={(val) => val && listIdField.onChange(parseInt(val, 10))}
            error={listIdState.error?.message}
            searchable
            required
          />

          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose}>Cancel</Button>
            <Button type="submit">
              {editSource ? 'Save' : 'Add Source'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
